/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect } from 'react';
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from 'react-intl';

import { TableContainer, Container, Grid, Table, TableBody, TableCell, TableRow, Typography, Box, Chip } from '@material-ui/core';
import classNames from "classnames";

import { ApplicationReducer, ApplicationState, UserPermission } from "../../../data/interfaces";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";
import { Status } from "../../../elements/Status";
import AppInfoBar from "../../shared/AppInfoBar";

import { useStyles } from './styles';

interface AppOverviewProps {
    applicationReducer: ApplicationReducer
}

function AppOverview(props: AppOverviewProps) {
    const classes = useStyles();
    const intl = useIntl();
    const { applicationReducer } = props;
    const appList = applicationReducer?.applications?.list;
    const application = appList?.length > 0 ? appList[0] : undefined;
    const isApplicationLoading = applicationReducer.isLoading;

    const noDescriptionText = intl.formatMessage({
        id:'application.overview.noDescriptionText',
        defaultMessage: 'No description available'
    });

    const OverviewContent = () => {
        if (isApplicationLoading) {
            return (
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div>
            );
        } else {
            return (
                <Container maxWidth="lg" className={classes.container} classes={{root: classes.componentPadding}}>
                    <Grid container={true} spacing={3}>
                        <Grid item={true} xs={12} md={12} lg={11}>
                            <TableContainer>
                                <Table className={classes.table}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell
                                                component='th'
                                                scope='row'
                                                className={classNames(classes.leftCol, classes.firstRow)}
                                            >
                                                <Typography variant='h5' className={classes.overviewKeys}>
                                                    <FormattedMessage
                                                        id='application.overview.description'
                                                        defaultMessage='Description'
                                                    />
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                component='th'
                                                scope='row'
                                                colSpan={2}
                                                className={classes.leftCol}
                                            >
                                                <Typography
                                                    variant='h5'
                                                    className={classes.overviewDescription}
                                                    data-testid='application-description'
                                                >
                                                    {application?.description ? application?.description :
                                                        <i> {noDescriptionText} </i>}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component='th' scope='row' className={classes.leftCol}>
                                                <Typography variant='h5' className={classes.overviewKeys}>
                                                    <FormattedMessage
                                                        id='application.overview.tokenType'
                                                        defaultMessage='Token Type'
                                                    />
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant='subtitle2'
                                                    className={classes.overviewValues}
                                                    data-testid='application-token-type'
                                                >
                                                    {application?.tokenType}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component='th' scope='row' className={classes.leftCol}>
                                                <Typography variant='h5' className={classes.overviewKeys}>
                                                    <FormattedMessage
                                                        id='application.overview.workflowStatus'
                                                        defaultMessage='Workflow Status'
                                                    />
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Status type={application?.status?.toLowerCase()} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component='th' scope='row' className={classes.leftCol}>
                                                <Typography variant='h5' className={classes.overviewKeys}>
                                                    <FormattedMessage
                                                        id='application.overview.applicationOwner'
                                                        defaultMessage='Application Owner'
                                                    />
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="inline-flex" alignItems="center">
                                                    <Typography
                                                        variant='subtitle2'
                                                        className={classes.overviewValues}
                                                        data-testid='application-owner'
                                                    >
                                                        { application?.ownerInfo?.displayName || application?.owner} &nbsp;
                                                    </Typography>
                                                    <Typography
                                                        variant='subtitle2'
                                                        className={classes.overviewOwnerEmail}
                                                        data-testid='application-owner-email' 
                                                    >
                                                        { application?.ownerInfo && `(${application?.ownerInfo?.email})`}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        { application?.sharedWith && application?.sharedWith.length > 0 && 
                                            <TableRow>
                                                <TableCell component='th' scope='row' className={classes.leftCol}>
                                                    <Typography variant='h5' className={classes.overviewKeys} 
                                                        data-testid='shared-user-emails' >
                                                        <FormattedMessage
                                                            id='application.overview.applicationOwner.sharedWith'
                                                            defaultMessage='Shared with'
                                                        />
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <Typography
                                                            variant='subtitle2'
                                                            className={classes.overviewOwnerEmail} >
                                                            { application?.sharedWith.map((permission: UserPermission, index: number) => {
                                                                return (
                                                                    <Chip 
                                                                        data-testid="app-share-email-addresses"
                                                                        key={index}
                                                                        label={permission.userEmail} 
                                                                        className={classes.emailChip} />
                                                                    );
                                                                })
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        }
                                        
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Container>
            );
        }
    };

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <AppInfoBar />
            <OverviewContent />
        </main>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        applicationReducer: state.applicationReducer,
    }
};

export default connect(mapStateToProps)(AppOverview);
